/**
 * Gift Automation — Fullstack Theme
 * Écoute cart:updated (CartUpdatedEvent natif Fullstack)
 * Ajoute/retire les cadeaux et re-dispatche cart:updated avec sections
 * pour que le CartDrawer se re-rende correctement.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'ga_managed_gifts';
  const POLL_INTERVAL = 2000;

  function getManagedGifts() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  function setManagedGifts(ids) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  }

  // Récupère le sectionId du cart-drawer pour forcer le re-render
  function getCartDrawerSectionId() {
    const drawer = document.querySelector('cart-drawer');
    return drawer ? drawer.dataset.sectionId : null;
  }

  // Ajoute des variants avec sections pour que Fullstack re-rende le drawer
  async function addItems(variantIds) {
    if (!variantIds.length) return null;
    const sectionId = getCartDrawerSectionId();
    const body = {
      items: variantIds.map(id => ({ id: Number(id), quantity: 1 })),
    };
    if (sectionId) {
      body.sections = sectionId;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Retire des variants avec sections
  async function removeItems(variantIds) {
    if (!variantIds.length) return null;
    const sectionId = getCartDrawerSectionId();
    const updates = {};
    variantIds.forEach(id => { updates[Number(id)] = 0; });
    const body = { updates };
    if (sectionId) {
      body.sections = sectionId;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Dispatche cart:updated exactement comme Fullstack le fait
  function dispatchCartUpdated(cartData) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: {
        resource: {},
        data: {
          itemCount: cartData ? cartData.item_count : undefined,
          source: 'gift-automation',
          sections: cartData ? cartData.sections : undefined,
        },
      },
    }));
  }

  // Verrou pour éviter les appels concurrents
  let syncing = false;

  async function syncGifts(rules) {
    if (syncing) return;
    syncing = true;

    try {
      const cart = await fetchCart();
      const cartVariantIds = cart.items.map(item => String(item.variant_id));
      const managed = getManagedGifts().map(String);

      for (const rule of rules) {
        const triggerPresent = cartVariantIds.includes(String(rule.triggerVariantId));
        const giftIds = rule.giftVariantIds.map(String).filter(Boolean);

        if (triggerPresent) {
          const toAdd = giftIds.filter(id => !cartVariantIds.includes(id));
          if (toAdd.length) {
            console.info('[GiftAutomation] Ajout cadeaux:', toAdd);
            const result = await addItems(toAdd);
            setManagedGifts([...new Set([...managed, ...toAdd])]);
            // Fetch cart mis à jour pour avoir item_count + sections
            const updatedCart = await fetch('/cart.js').then(r => r.json());
            if (result && result.sections) updatedCart.sections = result.sections;
            dispatchCartUpdated(updatedCart);
          }
        } else {
          const toRemove = giftIds.filter(id => managed.includes(id) && cartVariantIds.includes(id));
          if (toRemove.length) {
            console.info('[GiftAutomation] Retrait cadeaux:', toRemove);
            const result = await removeItems(toRemove);
            setManagedGifts(managed.filter(id => !toRemove.includes(id)));
            dispatchCartUpdated(result);
          }
        }
      }
    } catch (e) {
      console.error('[GiftAutomation] Erreur:', e);
    } finally {
      syncing = false;
    }
  }

  function readConfig() {
    const el = document.getElementById('gift-automation-config');
    if (!el) {
      console.warn('[GiftAutomation] #gift-automation-config introuvable.');
      return null;
    }
    const enabled = el.dataset.enabled !== 'false';
    if (!enabled) {
      console.info('[GiftAutomation] Désactivé.');
      return null;
    }
    const triggerVariantId = el.dataset.triggerVariant;
    const triggerProductId = el.dataset.triggerProduct;
    const giftsRaw = el.dataset.gifts || '';
    const giftVariantIds = giftsRaw.split(',').map(s => s.trim()).filter(Boolean);

    if (!triggerVariantId || !giftVariantIds.length) {
      console.warn('[GiftAutomation] Produits non configurés (triggerVariantId ou cadeaux manquants).');
      return null;
    }

    console.info('[GiftAutomation] Config OK — trigger:', triggerVariantId, '| cadeaux:', giftVariantIds);
    return [{ triggerVariantId, triggerProductId, giftVariantIds }];
  }

  function init() {
    const rules = readConfig();
    if (!rules) return;

    const sync = () => syncGifts(rules);

    // Event natif Fullstack — cart:updated
    document.addEventListener('cart:updated', (e) => {
      // Éviter la boucle infinie sur nos propres dispatches
      if (e.detail?.data?.source === 'gift-automation') return;
      sync();
    });

    // Polling fallback léger
    let lastCount = -1;
    setInterval(async () => {
      try {
        const cart = await fetchCart();
        if (cart.item_count !== lastCount) {
          lastCount = cart.item_count;
          sync();
        }
      } catch {}
    }, POLL_INTERVAL);

    // Sync initiale
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
